import { baseApi, PaginatedResponse } from 'sama-shared';
import type { AdminOrderPayment, AdminPayout, AdminPayoutDetails } from '../types/admin';
import type { GetOrderPaymentsParams, GetPayoutParams } from '../types/api';

export const financeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminPayouts: builder.query<PaginatedResponse<AdminPayout>, GetPayoutParams>({
      query: (params) => ({
        url: '/admin/payout',
        method: 'GET',
        params,
      }),
      // Tag granular por partnerId
      providesTags: (_result, _error, params) => [
        { type: 'Payout', id: params.partnerId || 'LIST' },
        'Payout',
      ],
    }),

    getAdminPayoutById: builder.query<AdminPayoutDetails, { id: string; partnerId: string }>({
      query: ({ id }) => ({
        url: `/admin/payout/${id}`,
        method: 'GET',
        query: { partnerId: id }, // Aseguramos que el partnerId se pase como query
      }),
      providesTags: (_result, _error, { partnerId }) => [
        { type: 'Payout', id: partnerId },
        'Payout',
      ],
    }),

    retryPartnerPayout: builder.mutation<any, { id: string; partnerId: string }>({
      query: ({ id }) => ({
        url: `/admin/payout/regenerate/process/${id}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { partnerId }) => [
        { type: 'Payout', id: partnerId },
        { type: 'Report', id: partnerId },
      ],
    }),

    getOrderPayments: builder.query<PaginatedResponse<AdminOrderPayment>, GetOrderPaymentsParams>({
      query: (params) => ({
        url: '/admin/payment',
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, params) => [
        { type: 'OrderPayment', id: params.partnerId || 'LIST' },
        'OrderPayment',
      ],
    }),

    manualVerifyEnqueue: builder.mutation<any, { paymentId: string; partnerId: string }>({
      query: ({ paymentId }) => ({
        url: `/admin/payment/reprocess-pending-payments/${paymentId}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { partnerId }) => [
        { type: 'OrderPayment', id: partnerId },
        'OrderPayment',
      ],
    }),

    manualUpdatePaymentStatus: builder.mutation<
      any,
      // PENDING/REVIEW: 2 aprueba, 3 rechaza. FAILED: 2 reactiva (reabre settlements y
      // devuelve la orden a PAID). El backend responde 409 si el pago ya es terminal.
      { paymentId: string; partnerId: string; status: 2 | 3; reference?: string }
    >({
      query: ({ paymentId, status, reference }) => ({
        url: `/admin/payment/${paymentId}`,
        method: 'PATCH',
        body: { status, reference },
      }),
      invalidatesTags: (_result, _error, { partnerId }) => [
        { type: 'OrderPayment', id: partnerId },
        'OrderPayment',
      ],
    }),

    bancaribeBCVSync: builder.mutation<any, void>({
      query: () => ({
        url: '/admin/finance/bancaribe/exchange-rate/consult',
        method: 'POST',
      }),
      invalidatesTags: ['Finance'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdminPayoutsQuery,
  useRetryPartnerPayoutMutation,
  useGetAdminPayoutByIdQuery,
  useGetOrderPaymentsQuery,
  useManualVerifyEnqueueMutation,
  useManualUpdatePaymentStatusMutation,
  useBancaribeBCVSyncMutation,
} = financeApi;
